import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
    fetchAppData,
    setNotifications,
    setWorkspace,
    setWorkspaceMembership,
    setWorkspaceMemberships,
    setMessage
} from "./system/app-slice";
import {useTranslate} from "./system/useTranslate";
import {Toast} from "primereact/toast";
import {ProgressSpinner} from "primereact/progressspinner";
import {Message} from "primereact/message";
import Auth from "./auth";
import {matchPath, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import PageNotFound from "./system/page-not-found";
import Sidebar from "./system/components/sidebar";
import Page from "./system/components/page";
import {
    getAuthToken,
    setApiErrorHandler,
    setApiUnauthorizedHandler,
    setHeaderToken,
    setWorkspaceMutationGuard,
} from "./system/api";
import {
    isNormalWorkspaceMessagingMutation,
    isMemberReportMutation,
    isOwnWorkspacePreferenceMutation,
    isPersonalFocusMutation,
    isContextIndependentPaymentRecoveryMutation,
    isContextIndependentWorkspaceOnboardingProvisioningMutation,
    isPaymentRecoveryMutation,
    isWorkspaceMediaAccessMutation,
} from "./system/api-request-guards";
import * as WhtsappApi from "./system/wtspp-api";
import {check, login, logout, saveUser} from "./auth/auth-slice";
import {Sidebar as PrimeSidebar} from "primereact/sidebar";
import PageDisabled from "./system/page-disabled";
import DoubleCheckMailer from "./pages/double-check-mailer";
import Toolbar from "./system/components/toolbar";
import './App.css';
import {pagesMap} from './pages'
import {PrimeReactProvider} from "primereact/api";
import UnderConstruction from "./system/under-construction";
import LoadingLottie from "./system/components/loading-lottie";
import Workspace from "./models/workspace/workspace";
import Init from "./pages/workspace/init";
import ProfileInit from "./auth/init";
import SelectWorkspaceDialog from "./pages/workspace/select-workspace-dialog";
import WorkspaceMembership from "./models/workspace-membership/workspace-membership";
import UserEventLog from "./models/user-event-log/user-event-log";
import AcceptDenyNotificationDialog from "./pages/components/accept-deny-notification-dialog";
import {isPast} from "./system/utils";
import {useWorkspace} from "./models/workspace/use-workspace";
import useWorkspaceMembership from "./models/workspace-membership/use-workspace-membership";
import useUserEventLog from "./models/user-event-log/use-user-event-log";
import {useProject} from "./models/project/use-project";
import {Button} from "primereact/button";
import {SpeedDial} from "primereact/speeddial";
import AiAssistantSidebar from "./system/components/ai-assistant-sidebar";
import HelpSidebar from "./pages/help/components/help-sidebar";
import OnboardingDock from "./system/components/onboarding-dock";
import Icon from "./system/components/icon";
import {setStorage} from "./system/storage";
import {OverlayPanel} from "primereact/overlaypanel";
import SelectWorkspace from "./pages/workspace/components/select-workspace";
import SubscriptionPage from "./pages/subscription";
import CustomersPage from "./pages/customers";
import {RealtimeProvider} from "./system/realtime/realtime-provider";
import useWorkspaceRealtimeSync from "./system/realtime/useWorkspaceRealtimeSync";
import PushBridge from "./system/push/push-bridge";
import NotificationLinkOpener from "./system/entity-sidebars/notification-link-opener";
import {translateSessionEndReason} from "./system/realtime/session-events";
import Can from "./system/can";
import {useWorkspaceRole} from "./system/use-workspace-role";
import {
    canAccessWorkspacePage,
    isWorkspaceContextIndependentPath,
    shouldBlockWorkspaceMutation,
} from "./system/workspace-permissions";
import Mobile from "./pages/mobile";
import {FocusSessionProvider, useFocusSession} from "./system/focus/focus-session-context";
import {useFocusClock} from "./system/focus/focus-clock";
import FocusMode from "./pages/dashboard/focus-mode";
import {useTask} from "./models/task/use-task";
import ForcedWorkspaceSelectionDialog from "./pages/workspace/forced-workspace-selection-dialog";
import WorkspaceCreate from "./pages/workspace-create";
import ErrorMonitoringBoundary from "./system/components/error-monitoring-boundary";
import PublicFormPage from "./modules/forms/public-form-page";
import {SubscriptionStatusBanner} from "./system/components/subscription-status/subscription-status";
import {
    addErrorBreadcrumb,
    initializeErrorMonitoring,
    updateErrorMonitoringContext,
} from "./system/error-monitoring";

const primeReactConfig = {
    pt: {
        button: {
            root: ({props}) => ({
                style: {
                    justifyContent: "center",
                    gap: props.icon && props.label ? ".5rem" : 0,
                },
            }),
            label: () => ({style: {flex: "0 1 auto"}}),
            icon: () => ({className: "m-0 flex-none align-self-center"}),
        },
    },
};

function ConfiguredPage({page}) {
    const {workspaceMembership, workspaceRole} = useWorkspaceRole();
    if (page.disabled) return <PageDisabled/>;

    const content = page.page
        ? React.createElement(pagesMap[page.page] || PageNotFound, {...page})
        : <Page {...page}/>;

    if (workspaceRole) {
        return canAccessWorkspacePage(workspaceRole, page) ? content : <PageDisabled/>;
    }
    if (workspaceMembership) return <PageDisabled/>;

    return (
        <Can permission={page.can} fallback={<PageDisabled/>}>
            {content}
        </Can>
    );
}

function WorkspaceRealtimeSyncBridge() {
    useWorkspaceRealtimeSync();
    return null;
}

function FocusRecoveryBridge() {
    const focus = useFocusSession();
    const {focusModeVisible, openFocusMode, closeFocusMode} = focus;
    const taskApi = useTask();
    const translate = useTranslate();
    const [recoveredTask, setRecoveredTask] = useState(null);
    const clockNow = useFocusClock(Boolean(focus.session));
    const sessionKey = focus.session?.documentId || focus.session?.id;

    useEffect(() => {
        if (sessionKey) openFocusMode();
    }, [openFocusMode, sessionKey]);

    useEffect(() => {
        const taskId = focus.session?.task?.documentId || focus.session?.task?.id;
        if (!taskId) {
            setRecoveredTask(null);
            return undefined;
        }
        let active = true;
        taskApi.getTask(taskId).then((task) => {
            if (active && task) setRecoveredTask(task);
        }).catch(() => {
            if (active) setRecoveredTask(null);
        });
        return () => { active = false; };
        // Task model hook methods do not have stable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focus.session?.task?.documentId, focus.session?.task?.id]);

    if (!focus.session?.task) return null;
    const updateTask = async (task, data) => {
        const updated = await taskApi.updateTask(task.documentId || task.id, data);
        await focus.refresh();
        return updated;
    };
    const task = recoveredTask || focus.session.task;
    return <FocusMode task={task} focusTasks={[task]} session={focus.session} elapsedSeconds={focus.getOwnElapsedSeconds(clockNow)} visible={focusModeVisible} onHide={closeFocusMode} onMutateTask={updateTask} onSessionMutation={focus.mutate} onSelectTask={() => {}} translate={translate}/>;
}

function WorkspaceBootstrapError({error, isOnline, loading, onRetry}) {
    const translate = useTranslate();
    const backendUnavailable = error?.isNetworkError || !error?.status || Number(error.status) >= 500;
    const type = !isOnline ? "offline" : backendUnavailable ? "backend" : "error";

    return (
        <div className="w-full h-full flex align-items-center justify-content-center p-3 rtl">
            <div className="w-full md:w-8 lg:w-6 xl:w-5 surface-card shadow-2 border-round-2xl p-4 md:p-5 flex flex-column align-items-center text-center gap-3">
                <div className="w-4rem h-4rem border-circle surface-100 flex align-items-center justify-content-center">
                    <i className={`pi ${type === "offline" ? "pi-wifi" : "pi-server"} text-3xl text-primary`}/>
                </div>
                <h2 className="m-0 text-xl text-900">{translate(`workspace-bootstrap.${type}.title`)}</h2>
                <span className="text-xs text-500 line-height-3">{translate(`workspace-bootstrap.${type}.description`)}</span>
                <Button className="text-xs gap-2 mt-2" icon="pi pi-refresh" label={translate("workspace-bootstrap.retry")} loading={loading} onClick={onRetry}/>
            </div>
        </div>
    );
}

function App(props) {
    const dispatch = useDispatch();
    const {config, prompt, pages, status, homePageIndex, isDesktop, message, isDeveloper, workspaceMembershipData, workspaceSelectionRequired, notifications} = useSelector((state) => state.app);
    const {user, authLoading, isAuthenticated, userChecked} = useSelector(state => state.auth);
    const authToken = getAuthToken(user);
    const authenticatedUserId = user?.documentId ?? user?.id ?? null;
    const hasAuthToken = Boolean(authToken);
    const hasAppConfig = Boolean(config);
    const [visibleSidebar, setVisibleSidebar] = useState(isDesktop);
    const [selectWorkspaceDialog, setSelectWorkspaceDialog] = useState(false);
    const [aiAssistantVisible, setAiAssistantVisible] = useState(false);
    const [helpSidebarVisible, setHelpSidebarVisible] = useState(false);
    const [acceptDenyDialogVisible, setAcceptDenyDialogVisible] = useState(false);
    const [isOnline, setIsOnline] = useState(() => navigator.onLine);
    const workspaceMembership = useWorkspaceMembership();
    const userEventLog = useUserEventLog();
    const workspaceMembershipRef = useRef(workspaceMembership);
    const userEventLogRef = useRef(userEventLog);
    const translate = useTranslate();
    const toast = useRef();
    const selectWorkspaceRef = useRef();
    const location = useLocation();
    const navigate = useNavigate();
    const {workspaceRole} = useWorkspaceRole();
    const host = window.location.hostname; // help.zofino.ir
    const sub = host.split(".")[0];        // help
    const workspaceStatus = workspaceMembershipData?.workspace?.workspaceStatus || "active";
    const workspaceRestricted = workspaceStatus !== "active";
    const workspacePaymentPending = workspaceStatus === "payment_pending";
    // const workspaceContentRestricted = workspaceRestricted && !(workspacePaymentPending && location.pathname === "/settings");
    const isDashboardHome = location.pathname === "/dashboard" || location.pathname === "/dashboard/";
    const workspaceContextIndependentPath = isWorkspaceContextIndependentPath(location.pathname);
    const workspaceContentRestricted = workspaceRestricted && !workspaceContextIndependentPath && !(workspacePaymentPending && location.pathname === "/settings");
    const roleReadOnly = shouldBlockWorkspaceMutation(workspaceRole);
    const workspaceContentRef = useRef(null);
    const aiSpeedDialItems = [
        {
            label: translate("ai.assistant.open"),
            icon: "pi pi-comments",
            command: () => setAiAssistantVisible(true),
        },
        {
            label: translate("ai.command-center.open"),
            icon: "pi pi-chart-line",
            command: () => navigate("/ai"),
        },
    ];

    const invalidateSession = useCallback((reason = "session_expired") => {
        dispatch(setMessage({severity: "warn", content: translateSessionEndReason(translate, reason)}));
        dispatch(logout({remote: false}));
    }, [dispatch, translate]);

    const notifySessionCreated = useCallback(() => {
        dispatch(setMessage({
            severity: "warn",
            content: translate("security.new-session-created-toast"),
        }));
    }, [dispatch, translate]);

    workspaceMembershipRef.current = workspaceMembership;
    userEventLogRef.current = userEventLog;

    const [extraPages, setExtraPages] = useState([])
    const hasCompletedProfile = Boolean(
        user?.confirmed
        && String(user?.firstname || "").trim()
        && String(user?.lastname || "").trim()
        && String(user?.fullname || "").trim()
        && user?.gender
    );



    useEffect(() => {
        dispatch(fetchAppData());
    }, [dispatch]);

    useEffect(() => initializeErrorMonitoring(), []);

    useEffect(() => {
        updateErrorMonitoringContext({
            workspaceDocumentId: workspaceMembershipData?.workspace?.documentId,
            membershipId: workspaceMembershipData?.documentId || workspaceMembershipData?.id,
            sessionKey: user?.session?.id || user?.deviceId,
            route: location.pathname,
        });
        addErrorBreadcrumb("navigation", {});
    }, [
        location.pathname,
        user?.deviceId,
        user?.session?.id,
        workspaceMembershipData?.documentId,
        workspaceMembershipData?.id,
        workspaceMembershipData?.workspace?.documentId,
    ]);

    useEffect(() => {
        const updateConnectionState = () => setIsOnline(navigator.onLine);
        window.addEventListener("online", updateConnectionState);
        window.addEventListener("offline", updateConnectionState);
        return () => {
            window.removeEventListener("online", updateConnectionState);
            window.removeEventListener("offline", updateConnectionState);
        };
    }, []);

    useEffect(() => {
        const element = workspaceContentRef.current;
        if (!element) return;
        if (workspaceContentRestricted) element.setAttribute("inert", "");
        else element.removeAttribute("inert");
        return () => element.removeAttribute("inert");
    }, [workspaceContentRestricted]);

    useEffect(() => {
        setWorkspaceMutationGuard((requestConfig) => {
            // Workspace creation switches the persisted workspace immediately, while
            // the hydrated membership can still describe the previous workspace.
            // Let only the narrow payment-recovery contract through on routes that
            // are explicitly independent of the active workspace; the backend still
            // authorizes the new workspace header and owner membership.
            const contextIndependentPaymentRecovery = isContextIndependentPaymentRecoveryMutation(
                requestConfig,
                workspaceContextIndependentPath,
            );
            const contextIndependentWorkspaceOnboardingProvisioning = isContextIndependentWorkspaceOnboardingProvisioningMutation(
                requestConfig,
                workspaceContextIndependentPath,
            );
            const operational = workspaceStatus === "active"
                || (
                    workspaceStatus === "payment_pending"
                    && isPaymentRecoveryMutation(requestConfig)
                );
            const messagingException = workspaceStatus === "active"
                && isNormalWorkspaceMessagingMutation(requestConfig);
            const personalFocusException = workspaceStatus === "active"
                && isPersonalFocusMutation(requestConfig);
            const memberReportException = workspaceStatus === "active"
                && isMemberReportMutation(requestConfig);
            const ownWorkspacePreference = isOwnWorkspacePreferenceMutation(requestConfig);
            const mediaRead = isWorkspaceMediaAccessMutation(requestConfig);
            return {
                allowed: contextIndependentPaymentRecovery
                    || contextIndependentWorkspaceOnboardingProvisioning
                    || ownWorkspacePreference
                    || mediaRead
                    || (operational && (!roleReadOnly || messagingException || personalFocusException || memberReportException)),
                message: translate("no-permission"),
            };
        });

        return () => setWorkspaceMutationGuard(null);
    }, [roleReadOnly, translate, workspaceContextIndependentPath, workspaceStatus]);

    useEffect(() => {
        if (
            workspaceRole?.role === "guest"
            && !workspaceContextIndependentPath
            && !["/goals", "/inbox"].includes(location.pathname)
        ) {
            navigate("/goals", {replace: true});
        }
    }, [location.pathname, navigate, workspaceContextIndependentPath, workspaceRole?.role]);

    useEffect(() => {
        setApiErrorHandler((error) => {
            if (/^\/?(auth|otp|user)\//i.test(error.url)) return;
            dispatch(setMessage({severity:"error", content:error.message}));
        });
        setApiUnauthorizedHandler((error) => {
            invalidateSession(error?.details?.reason || error?.code || "session_expired");
        });

        return () => {
            setApiErrorHandler(null);
            setApiUnauthorizedHandler(null);
        };
    }, [dispatch, invalidateSession]);

    useEffect(() => {
        const handleRefresh = (event) => dispatch(saveUser(event.detail));
        window.addEventListener("zofino:auth-refreshed", handleRefresh);
        return () => window.removeEventListener("zofino:auth-refreshed", handleRefresh);
    }, [dispatch]);

    useEffect(()=>{
        if(config){
            const themeLink = document.getElementById("theme-css");
            if (themeLink) {
                themeLink.href = config["dark-mode"]
                    ? process.env.PUBLIC_URL + "/assets/themes/dark-theme.css"
                    : process.env.PUBLIC_URL + "/assets/themes/light-theme.css";
            }

            if (config.language==="fa"){
                document.body.classList.add("lang-fa");
            }
            else {
                document.body.classList.remove("lang-fa");
            }

            document.title = `${translate(config['app-name'])}`;
            dispatch(check());

        }
    },[config])

    useEffect(()=>{
        if (authToken){
            setHeaderToken(`Bearer ${authToken}`);
        } else {
            setHeaderToken(null);
        }
    }, [authToken])

    useEffect(()=>{
        if (authenticatedUserId && hasAuthToken && hasAppConfig) {
            workspaceMembershipRef.current.getWorkspaceMemberships();
        }
    }, [authenticatedUserId, hasAppConfig, hasAuthToken])

    useEffect(() => {
        if (authenticatedUserId && hasAuthToken && hasAppConfig) {
            userEventLogRef.current.getUserEventLogs();
            return;
        }

        dispatch(setNotifications([]));
    }, [authenticatedUserId, dispatch, hasAppConfig, hasAuthToken]);

    useEffect(() => {
        const hasUnreadDialog = isAuthenticated && notifications?.some((item) => (
            !item?.isRead && item?.displayType === "accept-deny-dialog"
        ));
        setAcceptDenyDialogVisible(Boolean(hasUnreadDialog));
    }, [isAuthenticated, notifications]);

    useEffect(()=>{
        if (message){
            const content = typeof message.content === "string"
                ? translate(message.content)
                : message.content;
            toast.current?.show({life:8000, severity: message.severity, content: <span className="w-full">{content}</span>, contentClassName:"flex align-items-center"});
        }
    },[message, translate])


    const publicFormToken = matchPath({path: "/f/:token", end: true}, location.pathname)?.params?.token || null;

    const getPageConfigs=()=>{
        const _currentPageIndex = pages.findIndex((page) => page["nav-url"] && matchPath({path: page["nav-url"], end: true}, location.pathname));
        return pages[_currentPageIndex]??pages[homePageIndex];
    }
    const currentPageConfig = getPageConfigs();
    const chatbotEnabled = currentPageConfig?.chatbot === true;
    const helpEnabled = Boolean(currentPageConfig?.helps);

    useEffect(() => {
        if (!chatbotEnabled) setAiAssistantVisible(false);
    }, [chatbotEnabled]);

    useEffect(() => {
        if (!helpEnabled) setHelpSidebarVisible(false);
    }, [helpEnabled]);

    const view = (status) =>{
        if (config&&config["under-construction"]&&!isDeveloper){
            return <UnderConstruction/>
        }
        else {
            switch (status) {
                case 'loading':return (
                    <LoadingLottie/>
                );
                case 'succeeded':
                    // a public smart form (/f/<token>) is for anyone, outside sign-in and workspaces
                    if (publicFormToken) return <div className={`${config.dir} ${config.language==="fa"&&"lang-fa"} h-full w-full`}><PublicFormPage token={publicFormToken}/></div>;
                    return (
                        <div className={`${config.dir} ${config.language==="fa"&&"lang-fa"} flex flex-column h-full w-full `}>
                            <Toast className="pr-3" ref={toast} position="bottom-left"/>
                            <RealtimeProvider
                                token={authToken}
                                currentSessionId={user?.session?.id}
                                workspaceId={workspaceMembershipData?.workspace?.id}
                                workspaceDocumentId={workspaceMembershipData?.workspace?.documentId}
                                onSessionCreated={notifySessionCreated}
                                onSessionInvalidated={invalidateSession}
                            >
                            <FocusSessionProvider>
                            <WorkspaceRealtimeSyncBridge/>
                            {isAuthenticated && <PushBridge/>}
                            {isAuthenticated && workspaceMembershipData?.workspace && <NotificationLinkOpener workspaceOnly={!isDesktop}/>}
                            <FocusRecoveryBridge/>
                            {config.authorization?(isAuthenticated?(
                                <div  className="w-full h-full flex flex-1 align-items-center justify-content-center">
                                    {
                                        hasCompletedProfile?(
                                            <>
                                                {getPageConfigs().name!=="posts"&&workspaceMembership.status!=="succeeded"?(
                                                    workspaceMembership.status === "failed" ? (
                                                        <WorkspaceBootstrapError error={workspaceMembership.error} isOnline={isOnline} loading={workspaceMembership.loading} onRetry={workspaceMembership.getWorkspaceMemberships}/>
                                                    ) : (
                                                        <LoadingLottie/>
                                                    )
                                                ):getPageConfigs().name!=="posts"&&workspaceMembershipData==null?(
                                                    workspaceSelectionRequired && location.pathname === "/workspace/create"
                                                        ? <WorkspaceCreate/>
                                                        : <Init/>
                                                ):!isDesktop?(
                                                    <Mobile/>
                                                ):(
                                                    <div className="w-full h-full flex flex-1 align-items-center">
                                                        {(config.sidebar) && (
                                                            <div className={`h-full ${!getPageConfigs()?.sidebar && "p-hidden"}`}>
                                                                {isDesktop ? (<Sidebar extraPages={extraPages} expanded={visibleSidebar}/>):(
                                                                    <div className="flex lg:hidden shadow-2">
                                                                        <PrimeSidebar className="w-fit headerless-sidebar" dir={config.dir} showCloseIcon={false} position={`${config.dir==="rtl"?"right":"left"}`} visible={visibleSidebar} onHide={()=>{setVisibleSidebar(false)}}>
                                                                            <Sidebar expanded={true} onChange={(e)=>{setVisibleSidebar(false)}}/>
                                                                        </PrimeSidebar>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="h-full flex-1 flex flex-column overflow-hidden">
                                                            {getPageConfigs()?.toolbar&&<Toolbar readOnly={workspaceRestricted || roleReadOnly} onTitleClicked={(e)=>{ selectWorkspaceRef.current.toggle(e);{/*navigate(`/workspace`)*/}}}  setVisibleSidebar={setVisibleSidebar} visibleSidebar={visibleSidebar}/>}
                                                            {/* the subscription banner, only when action is needed; a locked workspace keeps its own banner below */}
                                                            {!workspaceRestricted && workspaceRole?.role !== "guest" && <SubscriptionStatusBanner workspace={workspaceMembershipData?.workspace?.documentId}/>}
                                                            {workspaceRestricted && (
                                                                <div className="w-full flex flex-column sm:flex-row align-items-stretch sm:align-items-center gap-2 px-3 py-2 border-bottom-1 border-200 surface-50">
                                                                    <Message className="flex-1 justify-content-start text-xs" severity="warn" text={translate(`workspace-status.${workspaceStatus}`)}/>
                                                                    {workspaceStatus === "payment_pending" && (
                                                                        <Button type="button" className="text-xs gap-2" icon="pi pi-credit-card" label={translate("workspace-status.complete-payment")} onClick={() => navigate("/settings")}/>
                                                                    )}
                                                                </div>
                                                            )}
                                                            <SelectWorkspace ref={selectWorkspaceRef} onChange={()=>{navigate(`/`)}}/>

                                                            <div className="flex-1 w-full overflow-auto relative">
                                                                <div
                                                                    ref={workspaceContentRef}
                                                                    className={`h-full w-full transition-duration-200 ${workspaceContentRestricted ? "pointer-events-none select-none opacity-50" : ""}`}
                                                                    aria-disabled={workspaceContentRestricted}
                                                                >
                                                                <Routes>
                                                                    <Route path="/" element={<ConfiguredPage page={pages[homePageIndex]}/>}/>
                                                                    {
                                                                        pages.map(e=>(
                                                                            <Route key={`${e.name}-${e['nav-url']}`} path={e['nav-url']} element={<ConfiguredPage page={e}/>}/>
                                                                        ))
                                                                    }
                                                                    <Route path="*" element={<PageNotFound/>}/>
                                                                </Routes>
                                                                </div>
                                                                {workspaceContentRestricted && (
                                                                    <div className="absolute top-0 left-0 w-full h-full z-5 flex align-items-center justify-content-center p-3">
                                                                        <div className="surface-card border-1 border-200 border-round-xl shadow-3 p-4 flex flex-column align-items-center text-center gap-3 max-w-30rem">
                                                                            <i className="pi pi-lock text-4xl text-orange-500"/>
                                                                            <span className="font-bold text-base">{translate("workspace-status.readonly-title")}</span>
                                                                            <span className="text-xs text-600 line-height-3">{translate(`workspace-status.${workspaceStatus}`)}</span>
                                                                            {workspaceStatus === "payment_pending" && (
                                                                                <Button type="button" className="text-xs gap-2" icon="pi pi-credit-card" label={translate("workspace-status.complete-payment")} onClick={() => navigate("/settings")}/>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {(helpEnabled || chatbotEnabled) && !isDashboardHome && !workspaceContentRestricted && workspaceRole?.role !== "guest" && (
                                                                    <div className="app-assistant-launchers flex align-items-end gap-2">
                                                                        {helpEnabled && <Button
                                                                            type="button"
                                                                            icon={<Icon icon="mi mi:help" className="text-lg"/>}
                                                                            aria-label={translate("slide-over.title")}
                                                                            className="border-circle w-3rem h-3rem p-0 shadow-3"
                                                                            style={{backgroundColor: "#0CC0CC", borderColor: "#0CC0CC"}}
                                                                            onClick={() => setHelpSidebarVisible(true)}
                                                                        />}
                                                                        {chatbotEnabled && <div className="app-ai-speed-dial-slot">
                                                                            <SpeedDial
                                                                                model={aiSpeedDialItems}
                                                                                direction="up"
                                                                                buttonClassName="shadow-3"
                                                                                showIcon="pi pi-sparkles"
                                                                                hideIcon="pi pi-times"
                                                                                aria-label={translate("ai.assistant.open")}
                                                                                tooltipOptions={{position: "right"}}
                                                                            />
                                                                        </div>}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <SelectWorkspaceDialog visible={selectWorkspaceDialog} setVisible={setSelectWorkspaceDialog}/>
                                                            {chatbotEnabled && !isDashboardHome && <AiAssistantSidebar visible={aiAssistantVisible} setVisible={setAiAssistantVisible}/>}
                                                            {!workspaceContentRestricted && ["owner", "admin"].includes(workspaceRole?.role) && <OnboardingDock/>}
                                                            <HelpSidebar visible={helpSidebarVisible} setVisible={setHelpSidebarVisible} pageConfig={currentPageConfig}/>
                                                            {/*<div className="flex-1 h-full flex overflow-hidden">*/}

                                                            {/*</div>*/}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ):(
                                            <div className="w-full h-full flex flex-1 align-items-center justify-content-center">
                                                <ProfileInit/>
                                            </div>
                                        )
                                    }
                                </div>
                            ):<Auth/>):(
                                <>
                                    {config.sidebar && (<Sidebar/>)}
                                    <Routes>
                                        {/*{pages.length>0&&}<Route path="/" element={<Page {...pages[0]}/>}/>*/}
                                        {
                                            pages.map(e=><Route key={`${e.name}-${e['nav-url']}`} path={e['nav-url']} element={<Page {...e}/>}/>)
                                        }
                                        <Route path="*" element={<PageNotFound/>}/>
                                    </Routes>
                                </>
                            )}
                            </FocusSessionProvider>
                            </RealtimeProvider>
                        </div>
                    );
                case 'failed':return (
                    <div>Error</div>
                )
                default: return(<div>{status}</div>)
            }
        }


    }

    return (

        <PrimeReactProvider value={primeReactConfig}>
            <ErrorMonitoringBoundary fallback={(
                <div className="w-screen h-screen flex align-items-center justify-content-center surface-ground rtl">
                    <div className="surface-card border-1 border-200 border-round-xl p-4 text-center text-sm">
                        {translate("error-monitoring-fallback")}
                    </div>
                </div>
            )}>
            <div className="w-screen h-screen overflow-hidden surface-ground flex align-items-center justify-content-center">
                {userChecked?view(status):view('loading')}
                {config&&<AcceptDenyNotificationDialog visible={acceptDenyDialogVisible} setVisible={setAcceptDenyDialogVisible}/>}
                {config && isAuthenticated && location.pathname !== "/workspace/create" && <ForcedWorkspaceSelectionDialog/>}

            </div>
            </ErrorMonitoringBoundary>
        </PrimeReactProvider>

    );
}

export default App;
